import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";

import ArrowCircleBrokenRightIcon from "@svg/new-design/arrow-circle-broken-right";
import ProfileMenuItem from "./ProfileMenuItem";
import { useLayoutContext } from "@context/layout";
import type { ReactNode } from "react";
import ProfileInfo from "./ProfileInfo";

interface Props {
  profileMenu: {
    id: string | undefined;
    name: string;
    icon: ReactNode;
    path: string;
    href: string;
  }[];
  logout: () => void;
  selected?: string;
  setSelected: (value: React.SetStateAction<string | undefined>) => void;
  profileName: string;
  mobile: string;
  email: string;
  showBanner?: boolean;
  showFestivalBanner?: boolean;
  isAdmin: boolean;
}

const MENU_HEIGHT = 768;

const ProfileSideMenu: React.FC<Props> = ({
  profileMenu,
  logout,
  selected,
  setSelected,
  profileName,
  mobile,
  email,
  showBanner,
  isAdmin,
}) => {
  const { userData } = useLayoutContext();
  const [isSticky, setIsSticky] = useState(false);

  const offsetTop = useMemo(() => {
    if (showBanner) return isAdmin ? 166 : 116;
    return isAdmin ? 110 : 80;
  }, [showBanner, isAdmin]);
  const stickyTop = useMemo(() => {
    if (typeof window === "undefined") return "";

    const topValue =
      window.innerHeight > offsetTop + MENU_HEIGHT
        ? offsetTop
        : window.innerHeight - MENU_HEIGHT;

    return `top-[${topValue}px]`;
  }, [offsetTop]);

  const filteredProfileMenu = useMemo(
    () =>
      profileMenu.filter((item) =>
        item.id === "instructor" ? Boolean(userData?.user?.is_instructor) : true
      ),
    [profileMenu, userData]
  );

  useEffect(() => {
    const triggerPoint = Math.abs(
      window.innerHeight - (MENU_HEIGHT + offsetTop)
    );

    const handleScroll = () => {
      setIsSticky(window.scrollY > triggerPoint);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [offsetTop]);

  return (
    <nav
      className={classNames(
        "flex flex-col overflow-y-auto scrollMenu justify-between rounded-16 border border-fds-default self-baseline py-4 bg-white min-w-[282px] max-w-[282px]",
        isSticky ? `sticky ${stickyTop}` : "relative"
      )}
      style={{ height: MENU_HEIGHT }}
    >
      <div className="w-full">
        <ProfileInfo profileName={profileName} email={email} mobile={mobile} />

        <ul className="px-4 mt-2">
          {filteredProfileMenu.map((item) => (
            <ProfileMenuItem
              key={item.id}
              item={item}
              selected={selected}
              onClick={() => setSelected(item.id)}
            />
          ))}
        </ul>
      </div>

      <li
        className="flex cursor-pointer rounded-l-8 hover:bg-fds-secondary mx-4 py-2 px-4 items-center justify-between"
        onClick={logout}
      >
        <p className="flex w-full items-center text-fds-primary gap-x-2">
          <ArrowCircleBrokenRightIcon />
          <span className="body-lg">خروج از حساب کاربری</span>
        </p>
      </li>
    </nav>
  );
};

export default ProfileSideMenu;
